import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    ArrowLeft,
    Clock,
    Wallet,
    ShieldCheck,
    ArrowUpRight,
    CheckCircle2,

    Server,
    Code2
} from 'lucide-react';

const caseStudiesData = {
    'global-payments': {
        title: 'Major Payment Processor',
        industry: 'Global Payments',
        description: 'Saving $2.5M annually by reducing incident response time.',
        heroImage: 'from-blue-600 to-indigo-700',
        stats: [
            { label: 'Response Time', value: '-40%', icon: Clock },
            { label: 'Annual Savings', value: '$2.5M', icon: Wallet },
            { label: 'Data Sources', value: '15+', icon: Server },
        ],
        challenge: `
      <p>A top-tier global payment processor handling billions in daily transactions faced a critical visibility gap. Their engineering teams used a fragmented toolchain—Jenkins for CI, Jira for tracking, and Datadog for logs—but lacked a unified view of quality health.</p>
      <p>This fragmentation meant that when production incidents occurred, "Incident Response Calls" took <b>45-60 minutes</b> just to assemble the right data to identify the root cause. In the payments world, every minute of downtime costs thousands in lost revenue and reputation.</p>
    `,
        solution: `
      <p>IronGate implemented <b>QE Navigator</b> to create a "Single Pane of Glass" for the entire engineering organization. We built custom aggregators that:</p>
      <ul class="list-disc pl-5 space-y-2 mt-4 mb-4">
        <li>Ingested real-time build status from <b>500+ Jenkins pipelines</b>.</li>
        <li>Correlated defect severity from Jira with active production alerts.</li>
        <li>Visualized deployment health across 12 geographic regions instantly.</li>
      </ul>
      <p>We introduced <b>Business Impact Correlation</b>, allowing executives to see not just "Server Down" alerts, but "Potential Revenue Impact: High" indicators, bridging the gap between Ops and Business leadership.</p>
    `,
        results: `
        <p>The impact was immediate and quantifiable:</p>
        <ul class="list-disc pl-5 space-y-2 mt-4">
          <li><b>MTTR (Mean Time To Resolution) dropped by 40%</b>, saving an average of 20 minutes per major incident.</li>
          <li>At their transaction volume, this translates to <b>$2.5M in prevented revenue loss</b> annually.</li>
          <li>Executive reporting time was reduced from <b>10 hours/week to zero</b> (fully automated).</li>
        </ul>
    `
    },
    'healthcare-compliance': {
        title: 'Medical Software Platform',
        industry: 'Healthcare',
        description: 'Modernizing legacy patient-care software to enable rapid, safe feature delivery.',
        heroImage: 'from-emerald-600 to-teal-700',
        stats: [
            { label: 'Regression Time', value: '-90%', icon: Clock },
            { label: 'Critical Bugs', value: '0', icon: ShieldCheck },
            { label: 'Release Cycle', value: '3x Faster', icon: ArrowUpRight },
        ],
        challenge: `
      <p>A major healthcare provider relied on a complex, decade-old application for patient management. Over time, the codebase had become fragile; fixing a bug in one module often silently broke critical functionality in another.</p>
      <p>Developers were paralyzed by the "Fear of Change." Because the software was used in clinical settings, trust was paramount. To ensure safety, the team relied on an exhaustive <b>2-week manual regression phase</b> before every release, stifling innovation and delaying urgency patches.</p>
    `,
        solution: `
      <p>IronGate re-engineered the testing strategy from the ground up. We moved away from manual "click-testing" to a robust automated safety net:</p>
      <ul class="list-disc pl-5 space-y-2 mt-4 mb-4">
        <li><b>Visualized Risk</b>: Used QE Navigator to map historical defects to specific code modules, identifying high-risk "hotspots".</li>
        <li><b>Automated Safety Nets</b>: Implemented 2,000+ atomic automated tests that run on every commit, catching regressions in minutes, not weeks.</li>
        <li><b>Shift-Left Quality</b>: Integrated quality checks directly into the developer workflow, preventing brittle code from ever reaching the main branch.</li>
      </ul>
    `,
        results: `
      <p>The team shifted from "Gatekeepers" to "Accelerators."</p>
      <ul class="list-disc pl-5 space-y-2 mt-4">
        <li><b>90% reduction</b> in regression testing time (from 2 weeks to 1 day).</li>
        <li><b>Zero critical defects</b> reached production in the 12 months post-implementation.</li>
        <li>Developers regained the confidence to refactor and modernize the legacy core, accelerating feature delivery by <b>300%</b>.</li>
      </ul>
    `
    },
    'fintech-growth': {
        title: 'High-Growth Fintech',
        industry: 'Fintech',
        description: 'Scaling from bi-weekly to daily deployments by eliminating flaky tests.',
        heroImage: 'from-violet-600 to-fuchsia-700',
        stats: [
            { label: 'Release Rate', value: 'Daily', icon: ArrowUpRight },
            { label: 'Flaky Tests', value: '-95%', icon: CheckCircle2 },
            { label: 'Dev Satisfaction', value: '4.8/5', icon: Code2 },
        ],
        challenge: `
      <p>A "Unicorn" status Fintech startup was growing its engineering team by 100% YoY, but their release velocity was crashing. An unstable test suite meant developers re-ran builds 4-5 times to get a green light.</p>
      <p>Trust in the CI pipeline was at zero. Developers were merging code without waiting for tests, leading to a <b>30% increase in production bugs</b>.</p>
    `,
        solution: `
      <p>We implemented <b>Flaky Test Intelligence</b>. We didn't just fix tests; we built a system to manage them:</p>
      <ul class="list-disc pl-5 space-y-2 mt-4 mb-4">
        <li><b>AI Pattern Recognition</b> analyzed 6 months of build history to identify non-deterministic failures.</li>
        <li><b>Auto-Quarantine</b>: Flaky tests were automatically moved to a "Sandbox" pipeline, unblocking the main merge train.</li>
        <li><b>Root Cause Hints</b>: The system provided stack trace comparisons to help devs fix the flakiness faster.</li>
      </ul>
    `,
        results: `
      <p>The engineering culture shifted from "fighting the tools" to "shipping code."</p>
      <ul class="list-disc pl-5 space-y-2 mt-4">
        <li><b>95% reduction</b> in false-negative build failures.</li>
        <li>Deployment frequency increased from <b>bi-weekly to daily</b>.</li>
        <li>Customer-reported bugs dropped by <b>30%</b> despite the faster pace.</li>
      </ul>
    `
    }
};

const CaseStudyDetail = () => {
    const { id } = useParams();
    const study = caseStudiesData[id];
    const challengeRef = React.useRef(null);

    React.useEffect(() => {
        if (challengeRef.current) {
            // Scroll to the challenge section with some offset for the fixed header
            const yOffset = -100; // Adjust for header
            const element = challengeRef.current;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({ top: y, behavior: 'auto' });
        }
    }, [id]);

    if (!study) {
        return (
            <div className="min-h-screen pt-20 flex flex-col items-center justify-center text-center px-4">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Case Study Not Found</h1>
                <Link to="/" className="text-primary-600 hover:text-primary-500 font-medium flex items-center">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                </Link>
            </div>
        );
    }

    return (
        <article className="min-h-screen pt-20 bg-gray-50 dark:bg-[#0a0e17] transition-colors duration-300">
            {/* Hero Header */}
            <div className={`relative py-24 bg-gradient-to-br ${study.heroImage}`}>
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute inset-0 circuit-pattern opacity-10" />
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link to="/#stories" className="inline-flex items-center text-white/80 hover:text-white mb-8 transition-colors">
                        <ArrowLeft className="w-5 h-5 mr-2" /> Back to Results
                    </Link>
                    <span className="block text-primary-200 font-semibold tracking-wider uppercase mb-3 text-sm">
                        {study.industry} Case Study
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                        {study.title}
                    </h1>
                    <p className="text-xl md:text-2xl text-white/90 max-w-3xl leading-relaxed">
                        {study.description}
                    </p>
                </div>
            </div>

            {/* Stats Bar */}
            <div className="relative z-20 -mt-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white dark:bg-[#1a1f2e] rounded-xl shadow-xl border border-gray-100 dark:border-gray-800 p-6 md:p-8">
                    {study.stats.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div key={index} className="flex items-center space-x-4 border-b md:border-b-0 md:border-r border-gray-100 dark:border-gray-700 last:border-0 pb-4 md:pb-0">
                                <div className="p-3 bg-primary-500/10 rounded-lg">
                                    <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                                </div>
                                <div>
                                    <div className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                <div className="space-y-16">
                    {/* Challenge Section */}
                    <section ref={challengeRef}>
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-1 h-8 bg-red-500 rounded-full" />
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">The Challenge</h2>
                        </div>
                        <div
                            className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: study.challenge }}
                        />
                    </section>

                    {/* Solution Section */}
                    <section>
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-1 h-8 bg-primary-500 rounded-full" />
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">The IronGate Solution</h2>
                        </div>
                        <div className="bg-white dark:bg-[#111621] border border-gray-100 dark:border-gray-800 rounded-2xl p-8 shadow-sm">
                            <div
                                className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: study.solution }}
                            />
                        </div>
                    </section>

                    {/* Results Section */}
                    <section>
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="w-1 h-8 bg-emerald-500 rounded-full" />
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">The Impact</h2>
                        </div>
                        <div
                            className="prose prose-lg dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: study.results }}
                        />
                    </section>

                    {/* CTA */}
                    <div className="bg-primary-600 rounded-2xl p-8 md:p-12 text-center text-white">
                        <h3 className="text-2xl md:text-3xl font-bold mb-4">Facing similar challenges?</h3>
                        <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
                            See how QE Navigator can transform your engineering organization's quality culture and business impact.
                        </p>
                        <Link
                            to="/#contact"
                            className="inline-block px-8 py-3 bg-white text-primary-600 rounded-lg font-bold hover:bg-gray-100 transition-colors shadow-lg"
                        >
                            Start Your Transformation
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default CaseStudyDetail;
